/**
 * Universal API Client
 * Central, secure communication layer for all backend services
 * Handles authentication, error handling, request/response interceptors
 */

import axios from 'axios';

const AUTH_FALLBACK_TOKEN_KEY = 'ggtrophy_auth_token';

function readFallbackToken() {
  if (typeof window === 'undefined') return null;
  try {
    return localStorage.getItem(AUTH_FALLBACK_TOKEN_KEY);
  } catch (error) {
    return null;
  }
}

function writeFallbackToken(token) {
  if (typeof window === 'undefined') return;
  try {
    if (token) {
      localStorage.setItem(AUTH_FALLBACK_TOKEN_KEY, token);
    } else {
      localStorage.removeItem(AUTH_FALLBACK_TOKEN_KEY);
    }
  } catch (error) {
    // ignore storage failures
  }
}

// Environment configuration
// All services must run through Strapi backend. Avoid hardcoded fallbacks.
const BACKEND_URL = (() => {
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL.replace(/\/$/, '');
  }
  if (typeof window !== 'undefined' && window.__STRAPI_URL__) {
    return String(window.__STRAPI_URL__).replace(/\/$/, '');
  }
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    const { origin, hostname } = window.location;
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';

    if (isLocalhost) {
      console.warn('[UniversalApiClient] REACT_APP_BACKEND_URL not set; using localhost origin for development.');
      return origin.replace(/\/$/, '');
    }
  }

  throw new Error('[UniversalApiClient] REACT_APP_BACKEND_URL is not configured. Set it to your Strapi Cloud URL.');
})();
const REQUEST_TIMEOUT = parseInt(process.env.REACT_APP_API_TIMEOUT) || 90000;

/**
 * Create axios instances for different services
 */
const createServiceClient = (baseURL, serviceName) => {
  const client = axios.create({
    baseURL,
    timeout: REQUEST_TIMEOUT,
    headers: {
      'Content-Type': 'application/json',
    },
    withCredentials: true,  // SECURITY: Enable cookies for httpOnly JWT auth
  });

  // Request interceptor - Logging
  client.interceptors.request.use(
    (config) => {
      const fallbackToken = readFallbackToken();
      if (fallbackToken && !config.headers?.Authorization) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${fallbackToken}`;
      }

      // Add request ID for tracking
      config.metadata = { 
        startTime: Date.now(),
        requestId: generateRequestId(),
        serviceName
      };

      console.log(`🚀 [${serviceName}] ${config.method.toUpperCase()} ${config.url}`, {
        requestId: config.metadata.requestId,
        params: config.params,
        data: config.data
      });

      return config;
    },
    (error) => {
      console.error(`❌ [${serviceName}] Request setup error:`, error);
      return Promise.reject(error);
    }
  );

  // Response interceptor - Handle responses and errors
  client.interceptors.response.use(
    (response) => {
      const duration = Date.now() - response.config.metadata.startTime;
      console.log(`✅ [${serviceName}] ${response.status} ${response.config.url}`, {
        requestId: response.config.metadata.requestId,
        duration: `${duration}ms`,
        dataSize: JSON.stringify(response.data).length
      });

      return response;
    },
    (error) => {
      const duration = error.config?.metadata?.startTime 
        ? Date.now() - error.config.metadata.startTime 
        : 0;

      console.error(`❌ [${serviceName}] ${error.response?.status || 'Network Error'}`, {
        requestId: error.config?.metadata?.requestId,
        url: error.config?.url,
        duration: `${duration}ms`,
        error: error.response?.data || error.message
      });

      if (error.response?.status === 401) {
        writeFallbackToken(null);
      }

      return Promise.reject(handleServiceError(error, serviceName));
    }
  );

  return client;
};

// Service client - All services now run through Strapi
const strapiClient = createServiceClient(`${BACKEND_URL}/api`, 'Strapi');

/**
 * Generate unique request ID
 */
function generateRequestId() {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Standardized error handler
 */
function handleServiceError(error, serviceName) {
  const standardError = {
    service: serviceName,
    timestamp: new Date().toISOString(),
    requestId: error.config?.metadata?.requestId,
  };

  if (error.response) {
    // Server responded with error
    standardError.status = error.response.status;
    standardError.message = error.response.data?.error?.message 
      || error.response.data?.message 
      || error.response.statusText;
    standardError.code = error.response.data?.error?.code || `HTTP_${error.response.status}`;
    standardError.details = error.response.data?.error?.details || error.response.data;

    // Handle specific error codes
    switch (error.response.status) {
      case 401:
        standardError.message = 'Authentication required. Please login again.';
        // Optionally trigger logout
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('auth:expired'));
        }
        break;
      case 403:
        standardError.message = 'Access forbidden. Insufficient permissions.';
        break;
      default:
        // Keep the default message already set above
        break;
      case 404:
        standardError.message = error.response.data?.message || 'Resource not found';
        break;
      case 429:
        standardError.message = 'Too many requests. Please try again later.';
        break;
      case 500:
        standardError.message = 'Server error. Please try again later.';
        break;
    }
  } else if (error.request) {
    // Request made but no response
    standardError.status = 0;
    standardError.code = 'NETWORK_ERROR';
    standardError.message = `Unable to connect to ${serviceName}. Please check your connection.`;
  } else {
    // Something else happened
    standardError.status = 0;
    standardError.code = 'UNKNOWN_ERROR';
    standardError.message = error.message || 'An unexpected error occurred';
  }

  return standardError;
}

/**
 * Universal API Client
 * Provides unified interface for all backend communications
 */
class UniversalApiClient {
  /**
   * Make authenticated request to Strapi
   */
  static async strapiRequest(method, endpoint, data = null, options = {}) {
    try {
      const config = {
        method,
        url: endpoint,
        ...options
      };

      if (data) {
        if (method.toLowerCase() === 'get') {
          config.params = data;
        } else {
          config.data = data;
        }
      }

      console.log(`[UniversalApiClient] ${method.toUpperCase()} ${endpoint}`, { params: config.params, data: config.data });

      const response = await strapiClient(config);
      
      console.log(`[UniversalApiClient] Response from ${endpoint}:`, {
        status: response.status,
        dataKeys: response.data ? Object.keys(response.data) : [],
        data: response.data
      });

      // Strapi may return { success, data } or just the data directly
      // If the backend already wrapped in { success, data }, pass it through
      // Otherwise, wrap it
      if (response.data && typeof response.data === 'object' && 'success' in response.data) {
        // Backend already provided success flag
        return response.data;
      }

      return {
        success: true,
        data: response.data,
        status: response.status
      };
    } catch (error) {
      console.error(`[UniversalApiClient] Error in ${method.toUpperCase()} ${endpoint}:`, {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });

      return {
        success: false,
        error: error,
        status: error.response?.status || error.status || 0
      };
    }
  }



  /**
   * GET request to Strapi
   */
  static strapiGet(endpoint, params = {}, options = {}) {
    return this.strapiRequest('get', endpoint, params, options);
  }

  /**
   * POST request to Strapi
   */
  static strapiPost(endpoint, data = {}, options = {}) {
    return this.strapiRequest('post', endpoint, data, options);
  }

  /**
   * PUT request to Strapi
   */
  static strapiPut(endpoint, data = {}, options = {}) {
    return this.strapiRequest('put', endpoint, data, options);
  }

  /**
   * DELETE request to Strapi
   */
  static strapiDelete(endpoint, options = {}) {
    return this.strapiRequest('delete', endpoint, null, options);
  }

  /**
   * Batch requests (parallel execution) - all through Strapi
   */
  static async batchRequests(requests) {
    try {
      const promises = requests.map(req => {
        const { method, endpoint, data, options } = req;
        return this.strapiRequest(method, endpoint, data, options);
      });

      const results = await Promise.allSettled(promises);
      
      return results.map((result, index) => ({
        index,
        ...requests[index],
        ...(result.status === 'fulfilled' 
          ? { success: true, data: result.value.data }
          : { success: false, error: result.reason })
      }));
    } catch (error) {
      console.error('Batch request error:', error);
      throw error;
    }
  }

  /**
   * Health check for Strapi (includes all integrated services)
   */
  static async healthCheck() {
    try {
      const check = await strapiClient.get('/');
      return {
        strapi: check.status === 200,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      return {
        strapi: false,
        timestamp: new Date().toISOString()
      };
    }
  }

  /**
   * Clear cached authentication
   */
  static clearAuth() {
    localStorage.removeItem('user');
    writeFallbackToken(null);
  }

  /**
   * Set authentication token
   */
  static setAuthToken(token) {
    writeFallbackToken(token || null);
  }

  /**
   * Get current authentication token
   */
  static getAuthToken() {
    return readFallbackToken();
  }
}

export default UniversalApiClient;
export { strapiClient };
